import { type FC, memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
// Import UI Components
import { Button } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';

interface Props {
    handleApply?: () => void;
    handleReset?: () => void;
    handleCancel?: () => void;
    hasCancel?: boolean;
    disabledApply?: boolean;
    disabledReset?: boolean;
}

const formRootSelector = '.crm-dynamic-segment-form';
const enterIgnoredTags = ['BUTTON', 'TEXTAREA'];

const FormApplyAndResetButtonContainer: FC<Props> = ({
    handleApply = noop,
    handleReset = noop,
    handleCancel = noop,
    hasCancel = false,
    disabledApply = false,
    disabledReset = false,
}) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const applyStateRef = useRef({ handleApply, disabledApply });

    applyStateRef.current = { handleApply, disabledApply };

    useEffect(() => {
        const formRoot = containerRef.current?.closest<HTMLElement>(formRootSelector);
        if (!formRoot) return;

        // Enter applies the filter the same way as the Apply button (the form's own validated handler)
        const applyOnEnter = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            const isModified = e.shiftKey || e.ctrlKey || e.altKey || e.metaKey;
            if (
                e.key !== 'Enter' ||
                e.defaultPrevented ||
                e.isComposing ||
                e.repeat ||
                isModified ||
                enterIgnoredTags.includes(target?.tagName)
            ) {
                return;
            }

            const { handleApply: apply, disabledApply: isApplyDisabled } = applyStateRef.current;
            if (isApplyDisabled) return;

            e.preventDefault();
            apply();
        };

        // Gene UI Dropdown and date inputs blur themselves after a selection, so Enter then fires on <body>
        const handleDocumentKeyDown = (e: KeyboardEvent) => {
            if (e.target === document.body || e.target === document.documentElement) {
                applyOnEnter(e);
            }
        };

        formRoot.addEventListener('keydown', applyOnEnter);
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            formRoot.removeEventListener('keydown', applyOnEnter);
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, []);

    return (
        <div ref={containerRef} className="crm-dynamic-segment-form-bottom">
            <Button disabled={disabledReset} appearance="minimal" onClick={() => handleReset()}>
                {t(l.Reset)}
            </Button>
            {hasCancel && (
                <Button appearance="outline" onClick={handleCancel}>
                    {t(l.Cancel)}
                </Button>
            )}
            <Button disabled={disabledApply} onClick={handleApply}>
                {t(l.Apply)}
            </Button>
        </div>
    );
};

export default memo(FormApplyAndResetButtonContainer);
